import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { User, Camera, Calendar, Heart, HeartOff, Medal, Ruler, Scale, Activity, TrendingUp, Crown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { InstallAppButton } from '@/components/IOSInstallPrompt';

function Perfil() {
  const { user, setUser } = useAuth();
  const [nombre, setNombre] = useState(user?.nombre || '');
  const [fechaNacimiento, setFechaNacimiento] = useState(user?.fecha_nacimiento || '');
  const [entrenoFavorito, setEntrenoFavorito] = useState(user?.entreno_favorito || '');
  const [entrenoMenosFavorito, setEntrenoMenosFavorito] = useState(user?.entreno_menos_favorito || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [misGalones, setMisGalones] = useState([]);
  
  // Estado Pro - Medidas corporales
  const [medidas, setMedidas] = useState({
    altura: user?.altura || '',
    peso: user?.peso || '',
    pectoral: user?.medidas?.pectoral || '',
    brazo: user?.medidas?.brazo || '',
    cintura: user?.medidas?.cintura || '',
    cadera: user?.medidas?.cadera || '',
    cuadriceps: user?.medidas?.cuadriceps || '',
    gemelo: user?.medidas?.gemelo || '',
    cuello: user?.medidas?.cuello || '',
    antebrazo: user?.medidas?.antebrazo || '',
    grasa_corporal: user?.medidas?.grasa_corporal || ''
  });
  const [medidasLoading, setMedidasLoading] = useState(false);
  const [medidasMessage, setMedidasMessage] = useState('');
  const [historial, setHistorial] = useState([]);
  const [showHistorial, setShowHistorial] = useState(false);

  const isPro = user?.es_pro || user?.role === 'entrenador';

  useEffect(() => {
    fetchMisGalones();
    if (isPro) {
      fetchHistorial();
    }
  }, [isPro]);

  const fetchMisGalones = async () => {
    try {
      const response = await axios.get(`${API}/mis-galones`);
      setMisGalones(response.data);
    } catch (error) {
      console.error('Error al cargar galones:', error);
    }
  };

  const fetchHistorial = async () => {
    try {
      const response = await axios.get(`${API}/users/me/medidas-historial`);
      setHistorial(response.data);
    } catch (error) {
      console.error('Error al cargar historial:', error);
    }
  };

  const handleMedidasChange = (field, value) => {
    setMedidas(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveMedidas = async () => {
    setMedidasLoading(true);
    setMedidasMessage('');
    try {
      // Convertir valores vacíos a null y strings a números
      const medidasToSend = {};
      Object.entries(medidas).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          medidasToSend[key] = parseFloat(value);
        }
      });
      
      await axios.put(`${API}/users/me/medidas`, medidasToSend);
      setMedidasMessage('¡Medidas guardadas correctamente!');
      fetchHistorial();
    } catch (error) {
      setMedidasMessage(error.response?.data?.detail || 'Error al guardar medidas');
    } finally {
      setMedidasLoading(false);
    }
  };

  const getColorClass = (color) => {
    const colorMap = {
      gold: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
      silver: 'bg-gray-400/20 border-gray-400 text-gray-300',
      bronze: 'bg-orange-600/20 border-orange-600 text-orange-400',
      blue: 'bg-blue-500/20 border-blue-500 text-blue-400',
      green: 'bg-green-500/20 border-green-500 text-green-400',
      red: 'bg-red-500/20 border-red-500 text-red-400',
      purple: 'bg-purple-500/20 border-purple-500 text-purple-400'
    };
    return colorMap[color] || colorMap.gold;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const formData = new FormData();
      if (nombre !== user?.nombre) {
        formData.append('nombre', nombre);
      }
      if (fechaNacimiento !== (user?.fecha_nacimiento || '')) {
        formData.append('fecha_nacimiento', fechaNacimiento);
      }
      if (entrenoFavorito !== (user?.entreno_favorito || '')) {
        formData.append('entreno_favorito', entrenoFavorito);
      }
      if (entrenoMenosFavorito !== (user?.entreno_menos_favorito || '')) {
        formData.append('entreno_menos_favorito', entrenoMenosFavorito);
      }
      if (selectedFile) {
        formData.append('foto_perfil', selectedFile);
      }

      const response = await axios.put(`${API}/auth/me`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Actualizar el contexto con los nuevos datos del usuario
      setUser(response.data);
      setMessage('¡Perfil actualizado correctamente!');
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Error al actualizar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = nombre !== user?.nombre || 
                     fechaNacimiento !== (user?.fecha_nacimiento || '') ||
                     entrenoFavorito !== (user?.entreno_favorito || '') ||
                     entrenoMenosFavorito !== (user?.entreno_menos_favorito || '') ||
                     selectedFile;

  const currentPhotoUrl = user?.foto_perfil 
    ? `${BACKEND_URL}${user.foto_perfil}` 
    : null;

  return (
    <div className="min-h-screen bg-dark-mk">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <h1 className="text-4xl font-bold text-gold mb-8" data-testid="perfil-title">
          Mi Perfil
        </h1>

        <Card className="card-mk border-gray-800">
          <CardHeader>
            <CardTitle className="text-white">Información Personal</CardTitle>
            <CardDescription className="text-gray-400">
              Actualiza tu foto de perfil y datos personales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Foto de perfil actual y nueva */}
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-32 h-32 rounded-full object-cover border-4 border-gold"
                    />
                  ) : currentPhotoUrl ? (
                    <img
                      src={currentPhotoUrl}
                      alt={user?.nombre}
                      className="w-32 h-32 rounded-full object-cover border-4 border-gold"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center border-4 border-gold">
                      <User className="w-16 h-16 text-gray-900" />
                    </div>
                  )}
                  <div className="absolute bottom-0 right-0 bg-gold rounded-full p-2">
                    <Camera className="w-5 h-5 text-gray-900" />
                  </div>
                </div>

                <div className="w-full space-y-2">
                  <Label htmlFor="foto" className="text-white">
                    {currentPhotoUrl ? 'Cambiar Foto de Perfil' : 'Agregar Foto de Perfil'}
                  </Label>
                  <Input
                    id="foto"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange}
                    className="bg-gray-900 border-gray-700 text-white hidden"
                    data-testid="input-foto-perfil"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 border-gray-600"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => handleFileChange(e);
                        input.click();
                      }}
                    >
                      📁 Galería
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 border-gray-600"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.capture = 'environment';
                        input.onchange = (e) => handleFileChange(e);
                        input.click();
                      }}
                    >
                      📷 Cámara
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400">
                    📸 Toma una foto o selecciona desde tu galería
                  </p>
                </div>
              </div>

              {/* Información del usuario */}
              <div className="space-y-4 pt-4 border-t border-gray-700">
                <div className="space-y-2">
                  <Label htmlFor="nombre" className="text-white">Nombre</Label>
                  <Input
                    id="nombre"
                    type="text"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                    data-testid="input-nombre"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fecha_nacimiento" className="text-white flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gold" />
                    Fecha de Nacimiento
                  </Label>
                  <Input
                    id="fecha_nacimiento"
                    type="date"
                    value={fechaNacimiento}
                    onChange={(e) => setFechaNacimiento(e.target.value)}
                    className="bg-gray-900 border-gray-700 text-white"
                    data-testid="input-fecha-nacimiento"
                  />
                </div>

                {/* Preferencias de entreno */}
                <div className="space-y-4 pt-4 border-t border-gray-700">
                  <h3 className="text-white font-medium">Preferencias de Entreno</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="entreno_favorito" className="text-white flex items-center gap-2">
                      <Heart className="h-4 w-4 text-red-400" />
                      Lo que más me gusta
                    </Label>
                    <Textarea
                      id="entreno_favorito"
                      value={entrenoFavorito}
                      onChange={(e) => setEntrenoFavorito(e.target.value)}
                      placeholder="Ej: Ejercicios de fuerza, carreras largas, trabajo en equipo..."
                      className="bg-gray-900 border-gray-700 text-white min-h-[80px]"
                      data-testid="input-entreno-favorito"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="entreno_menos_favorito" className="text-white flex items-center gap-2">
                      <HeartOff className="h-4 w-4 text-gray-400" />
                      Lo que menos me gusta
                    </Label>
                    <Textarea
                      id="entreno_menos_favorito"
                      value={entrenoMenosFavorito}
                      onChange={(e) => setEntrenoMenosFavorito(e.target.value)}
                      placeholder="Ej: Burpees, cardio intenso, ejercicios de equilibrio..."
                      className="bg-gray-900 border-gray-700 text-white min-h-[80px]"
                      data-testid="input-entreno-menos-favorito"
                    />
                  </div>
                </div>

                <div className="space-y-2 pt-4 border-t border-gray-700">
                  <Label className="text-white">Email</Label>
                  <Input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-400">
                    El email no se puede modificar
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Rol</Label>
                  <Input
                    value={user?.role === 'entrenador' ? 'Entrenador' : 'Miembro'}
                    disabled
                    className="bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Miembro desde</Label>
                  <Input
                    value={user?.fecha_registro ? new Date(user.fecha_registro).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
                    disabled
                    className="bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>

              {message && (
                <div className={`text-sm p-3 rounded ${message.includes('correctamente') ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'}`}>
                  {message}
                </div>
              )}

              <Button
                type="submit"
                className="w-full btn-primary-mk"
                disabled={loading || !hasChanges}
                data-testid="btn-guardar-perfil"
              >
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Mis Galones */}
        {misGalones.length > 0 && (
          <Card className="card-mk border-gray-800 mt-6">
            <CardHeader>
              <CardTitle className="text-gold flex items-center gap-2">
                <Medal className="h-5 w-5" />
                Mis Galones
              </CardTitle>
              <CardDescription className="text-gray-400">
                Reconocimientos obtenidos por tu esfuerzo y dedicación
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {misGalones.map((galon) => (
                  <div 
                    key={galon.id}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${getColorClass(galon.galon_color)} transition-transform hover:scale-105`}
                    title={galon.galon_descripcion}
                  >
                    <span className="text-2xl">{galon.galon_icono}</span>
                    <div>
                      <p className="font-medium">{galon.galon_nombre}</p>
                      {galon.mensaje && (
                        <p className="text-xs opacity-70">"{galon.mensaje}"</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sección PRO - Medidas Corporales */}
        {isPro ? (
          <Card className="card-mk border-amber-600/50 mt-6 bg-gradient-to-br from-gray-900 to-amber-950/20">
            <CardHeader>
              <CardTitle className="text-gold flex items-center gap-2">
                <Crown className="h-5 w-5 text-amber-400" />
                Medidas Corporales
                <span className="ml-2 text-xs bg-amber-600 text-black px-2 py-0.5 rounded-full font-bold">PRO</span>
              </CardTitle>
              <CardDescription className="text-gray-400">
                Controla tu evolución física con mediciones detalladas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Datos básicos */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <Ruler className="h-4 w-4" /> Altura (cm)
                  </Label>
                  <Input
                    type="number"
                    value={medidas.altura}
                    onChange={(e) => handleMedidasChange('altura', e.target.value)}
                    placeholder="175"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <Scale className="h-4 w-4" /> Peso (kg)
                  </Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={medidas.peso}
                    onChange={(e) => handleMedidasChange('peso', e.target.value)}
                    placeholder="75.5"
                    className="bg-gray-800 border-gray-700"
                  />
                </div>
              </div>

              {/* Medidas corporales */}
              <div>
                <Label className="text-gold mb-3 block flex items-center gap-1">
                  <Activity className="h-4 w-4" /> Perímetros (cm)
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { key: 'pectoral', label: 'Pecho' },
                    { key: 'brazo', label: 'Brazo' },
                    { key: 'antebrazo', label: 'Antebrazo' },
                    { key: 'cintura', label: 'Cintura' },
                    { key: 'cadera', label: 'Cadera' },
                    { key: 'cuadriceps', label: 'Muslo' },
                    { key: 'gemelo', label: 'Gemelo' },
                    { key: 'cuello', label: 'Cuello' },
                  ].map(({ key, label }) => (
                    <div key={key} className="space-y-1">
                      <Label className="text-gray-400 text-xs">{label}</Label>
                      <Input
                        type="number"
                        step="0.1"
                        value={medidas[key]}
                        onChange={(e) => handleMedidasChange(key, e.target.value)}
                        placeholder="--"
                        className="bg-gray-800 border-gray-700 h-9"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* % Grasa corporal */}
              <div className="space-y-2">
                <Label className="text-white">% Grasa corporal</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={medidas.grasa_corporal}
                  onChange={(e) => handleMedidasChange('grasa_corporal', e.target.value)}
                  placeholder="15.5"
                  className="bg-gray-800 border-gray-700 max-w-32"
                />
              </div>

              {medidasMessage && (
                <div className={`text-sm p-3 rounded ${medidasMessage.includes('correctamente') ? 'bg-green-900 text-green-200' : 'bg-red-900 text-red-200'}`}>
                  {medidasMessage}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={handleSaveMedidas}
                  disabled={medidasLoading}
                  className="btn-primary-mk"
                >
                  {medidasLoading ? 'Guardando...' : 'Guardar Medidas'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowHistorial(!showHistorial)}
                  className="border-gray-600"
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {showHistorial ? 'Ocultar' : 'Ver'} Historial
                </Button>
              </div>

              {/* Historial de medidas */}
              {showHistorial && historial.length > 0 && (
                <div className="mt-4 p-4 bg-gray-800/50 rounded-lg">
                  <h4 className="text-gold font-semibold mb-3">Historial de registros</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {historial.map((entry) => (
                      <div key={entry.id} className="text-sm flex justify-between items-center py-1 border-b border-gray-700">
                        <span className="text-gray-400">
                          {new Date(entry.fecha).toLocaleDateString('es-ES')}
                        </span>
                        <span className="text-white">
                          {entry.tipo === 'peso' ? (
                            <span>Peso: <strong>{entry.valor} kg</strong></span>
                          ) : (
                            <span>Medidas actualizadas</span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* Banner para usuarios no Pro */
          <Card className="card-mk border-gray-700 mt-6 bg-gradient-to-br from-gray-900 to-gray-800">
            <CardContent className="p-6 text-center">
              <Crown className="h-12 w-12 mx-auto mb-3 text-amber-400/50" />
              <h3 className="text-xl font-bold text-gold mb-2">Medidas Corporales</h3>
              <p className="text-gray-400 mb-4">
                Controla tu peso, perímetros y evolución física con gráficos detallados.
              </p>
              <p className="text-amber-400 font-semibold">
                ⭐ Función exclusiva para Clientes Pro
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Contacta con tu entrenador para más información
              </p>
            </CardContent>
          </Card>
        )}

        {/* Install App Button */}
        <div className="mt-4">
          <InstallAppButton />
        </div>
      </div>
    </div>
  );
}

export default Perfil;
